import { Head, Link, router, usePage } from '@inertiajs/react'
import { X } from 'lucide-react'
import { useState } from 'react'
import type { CompanyFunnelProps, FunnelBoardCard } from '@/types/company'
import type { SharedPageProps } from '@/types/shared'
import { CompanyHeader } from '@/components/Shared/CompanyHeader'
import { FlashBanner } from '@/components/Shared/FlashBanner'

function funnelHref({ team, position }: { team: number | null; position: number | null }): string {
  const params = new URLSearchParams()
  if (team) {
    params.set('team_id', String(team))
  }
  if (position) {
    params.set('position_id', String(position))
  }
  const query = params.toString()
  return query ? `/company/funnel?${query}` : '/company/funnel'
}

function movedOn(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function avatarInitials(name: string, slug: string | null): string {
  const trimmed = name.trim()
  if (trimmed) {
    const parts = trimmed.split(/\s+/)
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase()
  }
  return slug ? slug[0].toUpperCase() : '?'
}

function CardMoveActions({ card }: { card: FunnelBoardCard }) {
  const [confirming, setConfirming] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (card.moves.length === 0) {
    return null
  }

  const confirmingMove = card.moves.find((move) => move.toStatus === confirming)

  const submit = () => {
    if (!confirming) {
      return
    }
    router.post(
      '/company/funnel/move',
      { application_id: card.id, to_status: confirming },
      {
        onStart: () => setBusy(true),
        onFinish: () => {
          setBusy(false)
          setConfirming(null)
        },
      },
    )
  }

  if (confirming) {
    return (
      <div className="mt-3">
        <p className="mb-2 font-sans text-[13px] font-semibold text-foreground">
          {confirmingMove?.label ?? confirming}?
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="h-[34px] rounded-[9px] bg-primary px-3 font-sans text-[13px] font-semibold text-primary-foreground disabled:opacity-60"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setConfirming(null)}
            disabled={busy}
            className="h-[34px] rounded-[9px] border border-border px-3 font-sans text-[13px] font-semibold text-foreground disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {card.moves.map((move) => (
        <button
          key={move.toStatus}
          type="button"
          onClick={() => setConfirming(move.toStatus)}
          className="h-[34px] rounded-[9px] border border-border px-3 font-sans text-[13px] font-semibold text-foreground"
        >
          {move.label}
        </button>
      ))}
    </div>
  )
}

function RevealButton({ card, revealCredits }: { card: FunnelBoardCard; revealCredits: number }) {
  const disabled = !card.revealable || revealCredits < 1
  const label = !card.revealable
    ? 'No contact on file'
    : revealCredits < 1
      ? 'Get credits — contact Hyrin'
      : 'Reveal — 1 credit'
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => router.post('/company/reveals', { application_id: card.id })}
      className="mt-[10px] h-[30px] w-full rounded-[9px] border border-primary px-3 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-primary disabled:border-border disabled:text-muted-foreground"
    >
      {label}
    </button>
  )
}

function CandidateCard({ card, revealCredits }: { card: FunnelBoardCard; revealCredits: number }) {
  return (
    <div className="rounded-card border border-border bg-card p-[18px] shadow-sm">
      <div className="flex items-center gap-3">
        {card.revealed && card.candidateAvatarUrl ? (
          <img src={card.candidateAvatarUrl} alt="" className="size-[38px] shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex size-[38px] shrink-0 items-center justify-center rounded-full bg-primary font-heading text-[14px] font-bold text-primary-foreground">
            {avatarInitials(card.candidateName, card.candidateSlug)}
          </span>
        )}
        <div className="min-w-0">
          {card.revealed && card.candidateSlug ? (
            <a
              href={`/ind/${card.candidateSlug}`}
              className="block truncate font-sans text-[14px] font-semibold leading-[1.3] text-foreground hover:text-primary"
            >
              {card.candidateName}
            </a>
          ) : (
            <span className="block truncate font-sans text-[14px] font-semibold leading-[1.3] text-foreground">
              {card.candidateName}
            </span>
          )}
          {card.location && (
            <p className="truncate font-mono text-[11px] text-muted-foreground">{card.location}</p>
          )}
        </div>
      </div>
      {card.topSkills.length > 0 && !card.revealed && (
        <p className="mt-[8px] truncate font-mono text-[11px] text-muted-foreground">
          {card.topSkills.join(' · ')}
        </p>
      )}
      {card.revealed && card.contact && (
        <p className="mt-[8px] truncate font-mono text-[11px] text-primary">
          {[card.contact.email, ...card.contact.phones].filter(Boolean).join(' · ')}
        </p>
      )}
      <span className="mt-[10px] inline-flex rounded-full border border-border bg-background px-[10px] py-[4px] font-mono text-[11px] text-foreground">
        {card.position.title}
      </span>
      <p className="mt-[10px] font-mono text-[11px] text-muted-foreground">
        moved {movedOn(card.movedAt)}
      </p>
      {card.status === 'accepted' && (
        <span className="mt-[8px] inline-flex rounded-full bg-secondary px-[8px] py-[3px] font-mono text-[10px] uppercase tracking-[0.08em] text-primary">
          Accepted
        </span>
      )}
      {!card.revealed && <RevealButton card={card} revealCredits={revealCredits} />}
      <CardMoveActions card={card} />
    </div>
  )
}

export default function CompanyFunnel({ lanes, summary, activeFilters, revealCredits }: CompanyFunnelProps) {
  const { flash } = usePage<SharedPageProps>().props
  const boardEmpty = lanes.every((lane) => lane.applications.length === 0)

  return (
    <>
      <Head title="Hyrin funnel — Hyrin" />

      <div className="min-h-screen bg-muted">
        <CompanyHeader />
        <FlashBanner flash={flash} />

        <main className="mx-auto max-w-[1320px] px-6 py-[52px]">
          <p className="mb-[14px] font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            // pipeline
          </p>
          <h1 className="mb-[10px] font-heading text-[36px] font-bold leading-[1.06] tracking-[-0.025em] text-foreground">
            Hyrin funnel
          </h1>
          <p className="mb-[10px] font-sans text-base text-muted-foreground">
            Every candidate across your open roles, in one pipeline.
          </p>
          <p className="mb-[14px] font-mono text-[12px] text-muted-foreground">
            {summary.totalCandidates} {summary.totalCandidates === 1 ? 'candidate' : 'candidates'} ·{' '}
            {summary.positionsCount} {summary.positionsCount === 1 ? 'role' : 'roles'}
          </p>

          {(activeFilters.team || activeFilters.position) && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {activeFilters.team && (
                <span className="inline-flex items-center gap-[6px] rounded-full border border-border bg-background px-[10px] py-[5px] font-mono text-[11px] text-foreground">
                  Team · {activeFilters.team.name}
                  <Link
                    href={funnelHref({ team: null, position: activeFilters.position?.id ?? null })}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Clear team filter"
                  >
                    <X className="size-[12px]" />
                  </Link>
                </span>
              )}
              {activeFilters.position && (
                <span className="inline-flex items-center gap-[6px] rounded-full border border-border bg-background px-[10px] py-[5px] font-mono text-[11px] text-foreground">
                  Position · {activeFilters.position.title}
                  <Link
                    href={funnelHref({ team: activeFilters.team?.id ?? null, position: null })}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Clear position filter"
                  >
                    <X className="size-[12px]" />
                  </Link>
                </span>
              )}
            </div>
          )}

          <div className="mb-8 flex flex-wrap items-center justify-between gap-2">
            <span className="font-mono text-[12px] text-muted-foreground">
              Credits: <span className="font-semibold text-foreground">{revealCredits}</span>
            </span>
            <Link
              href="/company/board_lanes"
              className="font-mono text-[12px] text-primary hover:opacity-80"
            >
              Customise lanes →
            </Link>
          </div>

          {boardEmpty && (
            <div className="mb-6 rounded-card border border-dashed border-border p-[18px]">
              <p className="font-sans text-[13px] leading-[1.55] text-muted-foreground">
                No candidates in your pipeline yet — matched candidates land in your Shortlist lane.
              </p>
            </div>
          )}

          <div className="-mx-6 overflow-x-auto px-6 pb-3">
            <div className="flex items-stretch gap-[14px]">
              {lanes.map((lane) => (
                <section
                  key={lane.key}
                  className="flex w-[300px] shrink-0 flex-col rounded-card bg-lane p-[10px]"
                >
                  <p className="mb-3 px-[6px] pt-[4px] font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                    {lane.label} · {lane.applications.length}
                  </p>
                  <div className="flex flex-col gap-[10px]">
                    {lane.applications.map((card) => (
                      <CandidateCard key={card.id} card={card} revealCredits={revealCredits} />
                    ))}
                    {lane.applications.length === 0 && (
                      <p className="px-[6px] py-[10px] font-sans text-[13px] text-muted-foreground">
                        Nothing here yet
                      </p>
                    )}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
